// @file Surface posts store
import { trackEvent } from '@@/bits/analytics'
import { SELF_HARM_POST_ALERT } from '@@/bits/confirmation_dialog'
import { isAppUsing } from '@@/bits/flip'
import { removeHashidPrefix } from '@@/bits/hashid_helper'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { getVuexStore } from '@@/bits/pinia'
import {
  getCustomPropertyValue,
  hasCustomProperties,
  isPostEmpty,
  setCustomPropertyValue,
} from '@@/bits/post_properties'
import { isPostPinned, isPostPublished, isPostSubmitted } from '@@/bits/post_state'
import { escapeStringRegexp } from '@@/bits/regex'
import { getPostsByGroupByProperty } from '@@/bits/surface_posts_grouping_helper'
import { selfHarmHelplineUrl } from '@@/bits/url'
import { WallWishGroupByValue } from '@@/enums'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useScreenRecorderStore } from '@@/pinia/screen_recorder_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useSurfaceFilterStore } from '@@/pinia/surface_filter'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useSurfacePostSearchStore } from '@@/pinia/surface_post_search'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { WishApi } from '@@/surface/api/wish'
import { getNewUnpinSortIndex, getTopSortIndex } from '@@/surface/sorter'
import type {
  AttachmentProps,
  Cid,
  HashId,
  Id,
  Post,
  PostAttributes,
  PostColor,
  TranslatedWish,
  UpdatePostArgs,
  WallGroupPost,
} from '@@/types'
import { usePostSorter } from '@@/vuecomposables/surface_post_sorter'
import type { RootState } from '@@/vuexstore/surface/types'
import { captureException } from '@sentry/vue'
import { cloneDeep } from 'es-toolkit'
import { defineStore } from 'pinia'
import type { ComputedRef } from 'vue'
import { computed, ref } from 'vue'

export const useSurfacePostsStore = defineStore('surfacePosts', () => {
  const surfaceVuexStore = getVuexStore<RootState>()

  const screenRecorderStore = useScreenRecorderStore()
  const surfaceStore = useSurfaceStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
  const surfaceFilterStore = useSurfaceFilterStore()
  const surfacePostSearchStore = useSurfacePostSearchStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceTranslationsStore = useSurfaceTranslationsStore()
  const surfaceStartingStateStore = useSurfaceStartingStateStore()
  const contentPickerStore = useContentPickerStore()
  const surfaceShareLinksStore = useSurfaceShareLinksStore()
  const surfacePostActionStore = useSurfacePostActionStore()
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()

  const { sortPosts } = usePostSorter()

  const currentPostsCount = computed(() => currentSortedPosts.value.length)

  /*
   * POST COLLECTION GETTERS
   */

  const allPosts = computed<Post[]>(() => {
    const posts: Post[] = surfaceVuexStore?.getters['post/savedPostsArray'] ?? []

    return posts.filter((post) => {
      if (surfaceStore.isGoogleClassroomEmbedded && surfaceStore.googleClassroomAuthorIdFilter) {
        try {
          return isPostPublished(post) && Number(post.author_id) === Number(surfaceStore.googleClassroomAuthorIdFilter)
        } catch (error) {
          captureException(error)
        }
      }

      // Apply breakout filters first
      if (
        surfaceShareLinksStore.isSectionBreakout &&
        post.wall_section_id !== surfaceShareLinksStore.sectionBreakoutSection?.id
      ) {
        return false
      }

      if (surfaceShareLinksStore.isColorBreakout && post.color !== surfaceShareLinksStore.colorBreakoutPostColor) {
        return false
      }

      if (surfaceShareLinksStore.isAuthorBreakout && post.author_id !== surfaceCurrentUserStore.currentUser?.id) {
        return false
      }

      if (
        surfaceShareLinksStore.isSingleSelectBreakout &&
        surfaceShareLinksStore.singleSelectBreakoutProperty &&
        surfaceShareLinksStore.singleSelectBreakoutOptionId
      ) {
        const customPropValue = getCustomPropertyValue(post, surfaceShareLinksStore.singleSelectBreakoutProperty)
        if (customPropValue !== surfaceShareLinksStore.singleSelectBreakoutOptionId) {
          return false
        }
      }

      // Show all posts to moderators
      if (surfacePermissionsStore.canIModerate) return true
      // Show own posts to authors
      if (post.author_id === surfaceStartingStateStore.user.id) return true
      // Only show published posts to everyone else
      return isPostPublished(post)
    })
  })
  const allSortedPosts = computed<Post[]>(() => sortPosts(allPosts.value))
  const sortedPostCids = computed(() => allSortedPosts.value.map((p) => p.cid) ?? [])
  const currentSortedPosts = computed(() => sortPosts(currentPosts.value))

  /*
   * POST MAP GETTERS
   */

  const allSortedPostsByGroup: ComputedRef<{
    [x: string]: Post[] | undefined
    [x: number]: Post[] | undefined
  }> = computed(() => {
    const sortedGroupIds = surfaceWishArrangementStore.sortedGroups.map((group) => group.id).filter((id) => id != null)
    return getPostsByGroupByProperty(allSortedPosts.value, sortedGroupIds, surfaceWishArrangementStore.wishGroupBy)
  })
  // Map of group ID to posts. Order of groups not guaranteed.
  const postsByGroup: ComputedRef<{
    [x: string]: Post[] | undefined
    [x: number]: Post[] | undefined
  }> = computed(() => {
    const sortedGroupIds = surfaceWishArrangementStore.sortedGroups.map((group) => group.id).filter((id) => id != null)
    return getPostsByGroupByProperty(currentSortedPosts.value, sortedGroupIds, surfaceWishArrangementStore.wishGroupBy)
  })
  // Posts by groups, but in order by which groups are sorted.
  const postsBySortedGroups: ComputedRef<Post[][]> = computed(() => {
    return surfaceWishArrangementStore.sortedGroups
      .map((group) => (group.id != null ? postsByGroup.value[group.id] : []))
      .filter((group) => group != null) as Post[][]
  })

  const postsBySectionId = computed<{ [sectionId: number]: Post[] }>(
    () => surfaceVuexStore?.getters['post/postsBySectionId'] ?? {},
  )

  const postsByAuthorId = computed<{ [authorId: number]: Post[] }>(() => {
    const postsByAuthorId = {}
    allSortedPosts.value.forEach((post) => {
      if (post.author_id == null) return
      if (postsByAuthorId[post.author_id] == null) {
        postsByAuthorId[post.author_id] = []
      }
      postsByAuthorId[post.author_id].push(post)
    })
    return postsByAuthorId
  })

  const sortedPostCidsBySectionId = computed<Record<string, Cid[]>>(() => {
    if (surfaceStore.isSectioned) {
      const sortedPostCidsBySectionId = {}
      sortedPostCids.value.forEach((postCid) => {
        const post = postEntitiesByCid.value[postCid]
        if (post.wall_section_id == null || isPostEmpty(post)) return
        if (sortedPostCidsBySectionId[post.wall_section_id] == null) {
          sortedPostCidsBySectionId[post.wall_section_id] = []
        }
        sortedPostCidsBySectionId[post.wall_section_id].push(postCid)
      })
      return sortedPostCidsBySectionId
    }
    return {}
  })

  /*
   * OTHER POST GETTERS
   */
  const remotePosts = ref<Post[]>([])
  const rawPostEntitiesByCid = computed<{ [postCid: string]: Post }>(
    () => surfaceVuexStore?.state.post.postEntitiesByCid ?? {},
  )
  const postEntitiesByCid = computed<{ [postCid: string]: Post }>(
    () => surfaceVuexStore?.getters['post/postEntitiesByCid'] ?? {},
  )
  const savedPostEntitiesByCid = computed<{ [postCid: string]: Post }>(
    () => surfaceVuexStore?.getters['post/savedPostEntitiesByCid'] ?? {},
  )
  const postEntitiesById = computed<{ [postId: string]: Post }>(
    () => surfaceVuexStore?.getters['post/postEntitiesById'] ?? {},
  )
  const postEntities = computed<Post[]>(() => Object.values(postEntitiesByCid.value))
  const postEntitiesByHashId = computed<{ [hashedId: string]: Post }>(() => {
    const entities = {}
    postEntities.value.forEach((post) => {
      if (post.hashid != null) {
        const hashedId = removeHashidPrefix(post.hashid)
        entities[hashedId] = post
      }
    })
    return entities
  })
  const postCids = computed<string[]>(() => surfaceVuexStore?.getters['post/postCids'] ?? [])
  const newestPostCid = computed(() => surfaceVuexStore?.getters['post/newestPostCid'] ?? [])
  const publishedPosts = computed<Post[]>(() => surfaceVuexStore?.getters['post/publishedPosts'] ?? [])
  const unapprovedPosts = computed<Post[]>(() => {
    const savedPosts: Post[] = surfaceVuexStore?.getters['post/savedPostsArray'] ?? []
    return savedPosts.filter((post) => isPostSubmitted(post))
  })
  const pendingPosts = computed<Post[]>(() => surfaceVuexStore?.getters['post/pendingPosts'] ?? [])
  const pendingPostEntitiesByCid = computed<Record<Cid, Post>>(
    () => surfaceVuexStore?.getters['post/pendingPostEntitiesByCid'] ?? {},
  )

  const mostRecentlyChangedGeolocationPostCid = computed<Cid | null>(
    () => surfaceVuexStore?.getters['post/mostRecentlyChangedGeolocationPostCid'] ?? null,
  )

  const hasFetchedPosts = computed<boolean>(() => surfaceVuexStore?.getters['post/isInitialPaginatedDataLoadDone'])

  const postBeingEditedCid = computed<Cid | null>(() => surfaceVuexStore?.state.post.postBeingEditedCid ?? null)
  const postBeingEdited = computed<Post | null>(() => surfaceVuexStore?.getters['post/postBeingEdited'] ?? null)

  const hasPostsWithCustomProperties = computed(() => allPosts.value.some(hasCustomProperties))
  const hasPostsWithSubject = computed(() => allPosts.value.some((post) => post.subject))
  const hasPostsWithBody = computed(() => allPosts.value.some((post) => post.body))
  const hasPostsWithAttachments = computed(() =>
    allPosts.value.some((post) => {
      if (post.attachment != null && post.attachment !== '') return true
      return post.wish_content?.attachment_props?.type === 'poll' // special case for polls, attachment would be blank.
    }),
  )
  const hasPostsWithLocation = computed(() =>
    allPosts.value.some((post) => post.location_name != null && post.location_name !== ''),
  )

  function isExistingPost(cid: Cid): boolean {
    return savedPostEntitiesByCid.value[cid] != null
  }

  function canPinPost(post: Post): boolean {
    return (
      surfacePermissionsStore.canIModerate && // only editor+ can pin
      isPostPublished(post) && // only published posts can be pinned
      !surfaceStore.isFrozen
    )
  }

  function getPostByServerId(id: string): Post | null {
    return surfaceVuexStore?.getters['post/getPostByServerId'](id)
  }

  /*
   * POST SEARCH
   */

  const currentPosts = ref<Post[]>(allPosts.value)

  function updateCurrentPosts(payload: Post[]): void {
    currentPosts.value = payload
  }

  /*
   * POST ACTIONS
   */

  async function fetchPosts(): Promise<void> {
    await surfaceVuexStore?.dispatch('post/fetchPosts', { shouldResetState: false })
  }

  async function startNewPost({ attributes }: { attributes?: PostAttributes }): Promise<{ cid: Cid } | undefined> {
    // On breakout links we enforce/predefine attributes on any new post.
    if (surfaceShareLinksStore.isColorBreakout) {
      attributes = {
        ...attributes,
        color: surfaceShareLinksStore.colorBreakoutPostColor,
      }
    }

    if (
      surfaceShareLinksStore.isSingleSelectBreakout &&
      surfaceShareLinksStore.singleSelectBreakoutProperty &&
      surfaceShareLinksStore.singleSelectBreakoutOptionId
    ) {
      attributes = setCustomPropertyValue(
        attributes ?? {},
        surfaceShareLinksStore.singleSelectBreakoutProperty,
        surfaceShareLinksStore.singleSelectBreakoutOptionId,
      )
    }

    return await surfaceVuexStore?.dispatch('post/startNewPost', { attributes })
  }

  function sortPost(payload: {
    postCid: Cid
    previousCid?: Cid | null
    nextCid?: Cid | null
    sortIndex?: number | null
    sectionId?: number | null
    gapSize?: number | null
  }): void {
    void surfaceVuexStore?.dispatch('post/sortedPost', payload)
  }

  // Combined method that sorts a post and optionally updates its color if color is provided.
  function handlePostDropUpdate(payload: {
    postCid: Cid
    sortIndex: number | null
    sectionId: number | null | undefined
    gapSize: number | null | undefined
    color: PostColor
    customPropertyValue?: string | null
  }): void {
    const { postCid, sortIndex, sectionId, gapSize, color, customPropertyValue } = payload

    const isPostMovingToNoValueGroup =
      surfaceWishArrangementStore.isGroupBySingleSelect &&
      surfaceWishArrangementStore.customPropIdFromWishGroupBy != null &&
      customPropertyValue === null
    if (isPostMovingToNoValueGroup) {
      const postWithoutProperty = cloneDeep(postEntitiesByCid.value[postCid])
      if (postWithoutProperty.custom_properties != null) {
        postWithoutProperty.custom_properties[surfaceWishArrangementStore.customPropIdFromWishGroupBy] = undefined
      }
    }

    // Sort the post to its new position
    sortPost({
      postCid,
      sortIndex,
      sectionId,
      gapSize,
    })

    // If group by color and target color is provided, update the post color
    // Need to check against undefined because color can be null for default color
    if (color !== undefined) {
      surfacePostActionStore.changePostColor({
        postCid,
        newBgColor: color,
        groupByChanged: true,
      })
    } else if (customPropertyValue !== undefined) {
      if (surfaceWishArrangementStore.customPropIdFromWishGroupBy == null) return
      surfacePostActionStore.changePostCustomProperties({
        postCid,
        customProperties: {
          [surfaceWishArrangementStore.customPropIdFromWishGroupBy]: customPropertyValue,
        },
      })
    }
  }

  function resizedPost(payload: { postCid: Cid; size: { width: number } }): void {
    void surfaceVuexStore?.dispatch('post/resizedPost', payload)
  }

  async function savePost(payload: {
    attributes: Post | PostAttributes
    updatePostArgs?: UpdatePostArgs
  }): Promise<void> {
    await surfaceVuexStore?.dispatch('post/savePost', payload, { root: true })
  }

  async function addPost(post: Post | PostAttributes): Promise<void> {
    await surfaceVuexStore?.dispatch('post/addPost', post, { root: true })
  }

  async function updatePostSection(payload: { postCid: string; sectionId: number | string }): Promise<void> {
    await surfaceVuexStore?.dispatch('post/updatePostSection', payload, { root: true })
    await surfaceVuexStore?.dispatch('post/updateAfterPostCreated', { postCid: payload.postCid }, { root: true })
  }

  function updatePostGroup(payload: { postCid: string; groupId: WallGroupPost['id'] }): void {
    if (surfaceWishArrangementStore.isGroupBySection) {
      void updatePostSection({ postCid: payload.postCid, sectionId: payload.groupId })
    } else if (surfaceWishArrangementStore.isGroupByColor) {
      surfacePostActionStore.changePostColor({
        postCid: payload.postCid,
        newBgColor: payload.groupId === 'default' ? null : (payload.groupId as PostColor),
        groupByChanged: true,
      })
    } else if (surfaceWishArrangementStore.isGroupBySingleSelect) {
      if (surfaceWishArrangementStore.customPropIdFromWishGroupBy == null) return

      const isPostMovingToNoValueGroup =
        surfaceWishArrangementStore.isGroupBySingleSelect && payload.groupId === WallWishGroupByValue.NoValue
      if (isPostMovingToNoValueGroup) {
        const postWithoutProperty = cloneDeep(postEntitiesByCid.value[payload.postCid])
        if (postWithoutProperty.custom_properties != null) {
          postWithoutProperty.custom_properties[surfaceWishArrangementStore.customPropIdFromWishGroupBy] = undefined
        }
      }

      surfacePostActionStore.changePostCustomProperties({
        postCid: payload.postCid,
        customProperties: {
          [surfaceWishArrangementStore.customPropIdFromWishGroupBy]:
            payload.groupId === WallWishGroupByValue.NoValue ? null : (payload.groupId as string),
        },
      })
      void surfaceVuexStore?.dispatch(
        'post/updateAfterPostCreated',
        { postCid: payload.postCid, groupByChanged: true },
        { root: true },
      )
    }
  }

  function shouldDisplayPost(postCid: Cid): boolean {
    // For future reference, this function used to look like:
    // return currentSortedPostsCids.value.includes(postCid)
    //
    // But we ran into a bug where there are duplicate posts
    // in the padlet if `wishSortBy` is `shuffle`. This
    // `shouldDisplayPost` function called in the `v-show`
    // directive somehow causes a second render that passes
    // duplicate posts to the `SurfacePost` component.
    // We still don't know why, but it's better to avoid
    // referencing "sorted" post getters in this function.

    return currentPosts.value.some((p) => p.cid === postCid)
  }

  function shouldDisplayGroup(group: WallGroupPost): boolean {
    if (group.id === WallWishGroupByValue.NoValue && !allSortedPostsByGroup.value[group.id]?.length) return false

    // If we are not searching and no filters are selected, display the group
    if (surfacePostSearchStore.searchTerm === '' && !surfaceFilterStore.hasSelectedFilters) {
      return true
    }

    /* If we are searching or filters are selected, display the group if it has any posts that should be displayed
    or if its title matches the search term */
    const searchTermRegex = new RegExp(escapeStringRegexp(surfacePostSearchStore.searchTerm), 'i')

    return (
      (postsByGroup.value[group.id]?.some((p) => shouldDisplayPost(p.cid)) ?? false) ||
      searchTermRegex.test('title' in group ? group.title : '')
    )
  }

  function groupHasPinnedPosts(groupId: WallGroupPost['id']): boolean {
    const posts = allSortedPostsByGroup.value[groupId]
    if (posts == null) return false
    return posts.some(isPostPinned)
  }

  function isGroupHasUnpinnedPosts(groupId: WallGroupPost['id']): boolean {
    const posts = allSortedPostsByGroup.value[groupId]
    if (posts == null) return false
    return posts.some((p) => !isPostPinned(p))
  }

  async function postPollVote({
    wishId,
    pollId,
    pollChoiceIds,
  }: {
    wishId: Id
    pollId: Id
    pollChoiceIds: number[]
  }): Promise<void> {
    try {
      const { PollApi } = await import('@@/surface/api/poll')
      const updatedWish = await PollApi.vote({ wishId, pollId, pollChoiceIds })
      await surfaceVuexStore?.dispatch('post/updatePostInStore', updatedWish, { root: true })
    } catch {
      void globalSnackbarStore.genericFetchError()
    }
  }

  async function rejectPost(id?: Id, wishHashid?: HashId): Promise<void> {
    if (id == null || wishHashid == null) return

    void surfaceVuexStore?.dispatch('post/removePost', { id })

    await WishApi.reject({ wishHashid })
  }

  function showSelfHarmPostAlert({ wish_id }: { wish_id?: Id }): void {
    if (wish_id == null) return

    trackEvent('SurfaceSelfHarmWarning', 'Showed Self-harm dialog for post', wish_id)
    void useGlobalConfirmationDialogStore().openConfirmationDialog({
      ...SELF_HARM_POST_ALERT,
      afterConfirmActions: [() => navigateTo(selfHarmHelplineUrl(), { target: 'blank' })],
    })
  }

  function movePost(payload: { postCid: Cid; position: { left: number; top: number } }): void {
    void surfaceVuexStore?.dispatch('post/movedPost', payload, { root: true })
  }

  // pin/unpin posts
  function pinPost(postCid: Cid): void {
    void savePost({
      attributes: {
        cid: postCid,
        pinned_at: new Date().toISOString(),
        // We already sort pinned posts to the top but to avoid any issues
        // when dropping a post to between a pinned and an unpinned post
        // we also set the sort_index.
        // For pinning a post, we set it to the top value.
        sort_index: getTopSortIndex(),
      },
    })
    trackEvent('Posts', 'Pinned post')
  }

  function unpinPost(postCid: Cid): void {
    const { sortIndex, gapSize } = getNewUnpinSortIndex()
    void savePost({
      attributes: {
        cid: postCid,
        pinned_at: null,
        // We already sort pinned posts to the top but to avoid any issues
        // when dropping a post to between a pinned and an unpinned post
        // we also set the sort_index.
        // For unpinning a post, we set it to the value between the last
        // pinned post and the first unpinned post.
        sort_index: sortIndex,
      },
      updatePostArgs: {
        gap_size: gapSize,
      },
    })
    trackEvent('Posts', 'Unpinned post')
  }

  function startEditingPost(payload: { postCid: Cid }): void {
    const isScreenRecordingForCurrentPost =
      screenRecorderStore.isRecording &&
      contentPickerStore.destinationEntityType === 'Post' &&
      contentPickerStore.destinationEntityUid === payload.postCid

    if (isScreenRecordingForCurrentPost) {
      globalSnackbarStore.setSnackbar({
        message: __('You are currently editing this post'),
        notificationType: SnackbarNotificationType.error,
      })
      return
    }

    void surfaceVuexStore?.dispatch('post/startEditingPost', payload)
  }

  function stopEditingPost(): void {
    void surfaceVuexStore?.dispatch('post/stopEditingPost')
  }

  function resetMostRecentlyChangedGeolocationPostCid(): void {
    void surfaceVuexStore?.commit('post/SET_MOST_RECENTLY_CHANGED_GEOLOCATION_POST_CID', null)
  }

  async function deletePost(postCid: Cid, options?: { trackHistory: boolean; softDelete: boolean }): Promise<void> {
    await surfaceVuexStore?.dispatch('post/deletePost', { postCid, options })
  }

  function createPendingPost(payload: { postAttributes: PostAttributes }): void {
    void surfaceVuexStore?.dispatch('post/createPendingPost', payload)
  }

  function removePendingPosts(payload: { cids: Cid[] }): void {
    void surfaceVuexStore?.dispatch('post/removePendingPosts', payload)
  }

  function updatePostInStore(payload: PostAttributes): void {
    void surfaceVuexStore?.dispatch('post/updatePostInStore', payload)
  }

  function clearAllPosts(): void {
    void surfaceVuexStore?.dispatch('post/clearAllPosts')
  }

  /**
   * Create post immediately. Example scenarios: paste/drop to post and in composer mode
   */
  function createPostImmediately(payload: { attributes: PostAttributes }): void {
    void surfaceVuexStore?.dispatch('post/createPost', payload)
  }

  function saveContent(payload: { content: AttachmentProps }): void {
    void surfaceVuexStore?.dispatch('post/saveContent', payload)
  }

  function saveAttachmentCaption(payload: { caption: string }): void {
    void surfaceVuexStore?.dispatch('post/saveAttachmentCaption', payload)
  }

  // REMOTE POST ACTIONS
  function newPostRemote(payload: { post: Post }): void {
    remotePosts.value = [...remotePosts.value, payload.post]
    if (surfaceTranslationsStore.isViewingTranslatedPadlet && payload.post.id != null) {
      void surfaceTranslationsStore.translateWishEntity(payload.post.id)
    }
  }

  function editPostRemote(payload: { post: Post }): void {
    remotePosts.value = remotePosts.value.map((post) => (post.id === payload.post.id ? payload.post : post))
    if (surfaceTranslationsStore.isViewingTranslatedPadlet && payload.post.id != null) {
      void surfaceTranslationsStore.translateWishEntity(payload.post.id)
    }
  }

  function deletePostRemote(payload: { post: Post }): void {
    remotePosts.value = remotePosts.value.filter((post) => post.id !== payload.post.id)
    if (surfaceTranslationsStore.isViewingTranslatedPadlet && payload.post.id != null) {
      void surfaceTranslationsStore.deleteWishFromTranslatedWall(payload.post.id)
    }
  }

  function updatePostsWithTranslations(payload: TranslatedWish[]): void {
    const posts = allPosts.value
    payload.forEach((translation) => {
      const post = posts.find((p) => p.id === Number(translation.wishId))
      if (post != null) {
        post.subject = translation.subject
        post.body = translation.body
        if (translation.attachmentCaption != null) {
          post.attachment_caption = translation.attachmentCaption
        }
        if (translation.customProperties != null) {
          post.custom_properties = translation.customProperties
        }
        if (post.wish_content?.attachment_props != null && translation.attachmentProps?.pollChoices != null) {
          post.wish_content.attachment_props.question = translation.attachmentProps.question
          translation.attachmentProps.pollChoices.forEach((choice) => {
            const pollChoice = post.wish_content?.attachment_props?.poll_choices?.find(
              (c) => c.poll_choice_id === choice.id,
            )
            if (pollChoice != null) {
              pollChoice.choice_text = choice.choiceText
            }
          })
        }

        updatePostInStore(post)
      }
    })
  }

  // #region UNDO/REDO DELETE POSTS
  async function undoDeletePosts(posts: Post[]): Promise<Post[]> {
    if (!isAppUsing('undoRedoDeleteActions')) return posts

    await Promise.all(
      posts.map(async (post) => {
        return await surfaceVuexStore?.dispatch('post/restorePost', { post })
      }),
    )
    return posts
  }

  async function redoDeletePosts(posts: Post[]): Promise<Post[]> {
    if (!isAppUsing('undoRedoDeleteActions')) return posts

    await Promise.all(
      posts.map(async (post) => {
        await surfaceVuexStore?.dispatch('post/deletePost', {
          postCid: post.cid,
          options: { trackHistory: false, softDelete: true },
        })
      }),
    )
    return posts
  }
  // #endregion

  function rejectPostsWithFileSizeExceeded({ file }: { file: File }): void {
    void surfaceVuexStore?.dispatch('post/rejectPostsWithFileSizeExceeded', { file })
  }

  function pickSection({ sectionId }: { sectionId: number }): void {
    void surfaceVuexStore?.dispatch('post/pickSection', { sectionId })
  }

  function cancelPickingSection(): void {
    void surfaceVuexStore?.dispatch('post/cancelPickingSection')
  }

  return {
    // POST COUNT GETTERS
    currentPostsCount,

    // POST COLLECTION GETTERS
    allPosts, // all posts, not sorted
    allSortedPosts, // all posts, sorted
    sortedPostCids,
    currentPosts: computed(() => currentPosts.value), // filtered posts, not sorted. exported as computed so it can't be mutated.
    currentSortedPosts, // filtered posts, sorted

    // POST MAP GETTERS
    allSortedPostsByGroup, // map of all posts by group, sorted
    postsByGroup, // map of filtered posts by group, sorted
    postsBySortedGroups, // 2D array of filtered posts, sorted
    postsBySectionId, // map of posts by section ID
    postsByAuthorId, // map of posts by author ID
    sortedPostCidsBySectionId, // map of post CIDs by section ID, sorted

    // OTHER POST GETTERS
    hasPostsWithCustomProperties,
    hasPostsWithSubject,
    hasPostsWithAttachments,
    hasPostsWithBody,
    hasPostsWithLocation,
    postEntitiesByCid,
    savedPostEntitiesByCid,
    rawPostEntitiesByCid,
    postEntitiesById,
    postEntities,
    postCids,
    newestPostCid,
    publishedPosts,
    unapprovedPosts,
    pendingPosts,
    pendingPostEntitiesByCid,
    mostRecentlyChangedGeolocationPostCid,
    hasFetchedPosts,
    postBeingEditedCid,
    postBeingEdited,
    isExistingPost,
    canPinPost,
    getPostByServerId,
    postEntitiesByHashId,
    remotePosts,
    resizedPost,

    // POST ACTIONS
    fetchPosts,
    startNewPost,
    sortPost,
    handlePostDropUpdate,
    shouldDisplayPost,
    shouldDisplayGroup,
    groupHasPinnedPosts,
    isGroupHasUnpinnedPosts,
    postPollVote,
    savePost,
    addPost,
    updatePostSection,
    updatePostGroup,
    rejectPost,
    showSelfHarmPostAlert,
    movePost,
    pinPost,
    unpinPost,
    stopEditingPost,
    resetMostRecentlyChangedGeolocationPostCid,
    deletePost,
    startEditingPost,
    createPendingPost,
    removePendingPosts,
    updateCurrentPosts,
    updatePostInStore,
    createPostImmediately,
    saveContent,
    saveAttachmentCaption,
    newPostRemote,
    editPostRemote,
    deletePostRemote,
    updatePostsWithTranslations,
    undoDeletePosts,
    redoDeletePosts,
    clearAllPosts,
    rejectPostsWithFileSizeExceeded,
    pickSection,
    cancelPickingSection,
  }
})
